// src/components/demo/DemoFitnessPage.js
import React, { useState, useRef, useEffect } from 'react';
import EnhancedCamera from '../exercises/EnhancedCamera';
import '../daily/DailyPlanPage.css';
import './DemoCelebration.css';

const TaskIcon = ({ type, completed }) => {
  const style = completed ? { filter: 'brightness(0) invert(1)' } : {};
  const src = type === 'strength' ? '/icon-strength.svg'
    : type === 'athletics' ? '/icon-athletics.svg'
    : '/icon-nutrition.svg';
  return <img src={src} alt={type} width="22" height="22" style={style} />;
};

const DonutProgress = ({ completed, total }) => {
  const size = 72;
  const strokeWidth = 12;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = total > 0 ? (completed / total) * circumference : 0;
  const offset = circumference - progress;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="rgb(255, 255, 255)" strokeWidth={strokeWidth}/>
      <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#28a745" strokeWidth={strokeWidth}
        strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`} style={{ transition: 'stroke-dashoffset 0.4s ease' }}/>
    </svg>
  );
};

const DemoFitnessPage = ({ onBack }) => {
  const [language, setLanguage] = useState(() =>
    localStorage.getItem('spocademy_language') || 'mr'
  );
  const [completedTasks, setCompletedTasks] = useState(new Set());
  const [currentExercise, setCurrentExercise] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const audioContextRef = useRef(null);
  const clapRef = useRef(null);
  const celebrationHandledRef = useRef(false);

  const translations = {
    en: {
      day: 'Day', markComplete: 'Mark Complete', startCamera: 'Start Camera',
      completed: '✓ Completed', back: '← Back', shabash: 'Shabash!', shabashBtn: 'Continue'
    },
    mr: {
      day: 'दिवस', markComplete: 'पूर्ण झाले', startCamera: 'कॅमेरा सुरू करा',
      completed: '✓ पूर्ण झाले', back: '← मागे', shabash: 'शाबास!', shabashBtn: 'पुढे'
    }
  };
  const t = translations[language];

  const TASKS = [
    {
      id: 'squats', type: 'strength', hasMediaPipe: true, mediaPipeType: 'squats',
      name: { mr: 'स्क्वॅट्स', en: 'Squats' }, reps: 3, sets: 2, restTime: 10
    },
    {
      id: 'running', type: 'athletics', hasMediaPipe: false,
      name: { mr: '१०० मीटर धावणे', en: '100m Running' }, reps: 6, sets: 2, restTime: 120
    },
    {
      id: 'food', type: 'nutrition', hasMediaPipe: false,
      name: { mr: '१ केळी + २ अंडी', en: '1 Banana + 2 Eggs' }
    }
  ];

  const handleLanguageToggle = () => {
    const newLang = language === 'en' ? 'mr' : 'en';
    setLanguage(newLang);
    localStorage.setItem('spocademy_language', newLang);
  };

  const initAudio = () => {
    if (!audioContextRef.current) {
      try {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      } catch (e) {}
    }
  };

  const triggerHaptic = (type) => {
    if (!navigator.vibrate) return;
    if (type === 'light') navigator.vibrate(50);
    if (type === 'success') navigator.vibrate([100, 50, 100, 50, 200]);
  };

  const playSuccessSound = () => {
    initAudio();
    if (!audioContextRef.current) return;
    const ctx = audioContextRef.current;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.setValueAtTime(523, ctx.currentTime);
    osc.frequency.setValueAtTime(784, ctx.currentTime + 0.1);
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.3);
  };

  const playClap = () => {
    try {
      const a = new Audio('/claps.mp3');
      clapRef.current = a;
      a.play().catch(() => {});
    } catch (e) {}
  };

  const handleTaskComplete = (taskId) => {
    if (completedTasks.has(taskId)) return;
    initAudio();
    triggerHaptic('light');
    playSuccessSound();
    setCompletedTasks(prev => new Set([...prev, taskId]));
  };

  useEffect(() => {
    if (completedTasks.size === TASKS.length && !celebrationHandledRef.current) {
      celebrationHandledRef.current = true;
      triggerHaptic('success');
      setTimeout(() => {
        setShowCelebration(true);
        playClap();
      }, 600);
    }
  }, [completedTasks, TASKS.length]);

  const getTaskDescription = (task) => {
    if (!task.sets && !task.reps) return null;
    const parts = [];
    if (task.sets && task.reps) {
      parts.push(language === 'mr'
        ? `${task.sets} सेट × ${task.reps} रेप्स`
        : `${task.sets} sets × ${task.reps} reps`);
    }
    if (task.restTime) {
      parts.push(language === 'mr'
        ? `${task.restTime} सेकंद विश्रांती`
        : `${task.restTime}s rest`);
    }
    return parts.join(' · ');
  };

  const renderTaskCard = (task) => {
    const isCompleted = completedTasks.has(task.id);
    const taskName = task.name[language] || task.name.en;
    const description = getTaskDescription(task);
    const useCamera = task.hasMediaPipe;

    return (
      <div key={task.id} className={`task-card ${isCompleted ? 'completed' : ''}`}>
        <div className="task-header">
          <div className="task-icon-wrap">
            <TaskIcon type={task.type} completed={isCompleted} />
          </div>
          <div className="task-info">
            <div className="task-name">{taskName}</div>
            {description && <div className="task-description">{description}</div>}
          </div>
        </div>
        <div className="task-controls">
          {isCompleted ? (
            <div className="completed-indicator">{t.completed}</div>
          ) : (
            <button className="start-btn" onClick={() => {
              if (useCamera) setCurrentExercise(task);
              else handleTaskComplete(task.id);
            }}>
              {useCamera ? t.startCamera : t.markComplete}
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="daily-plan-container">
      <div className="dp-header">
        <div className="dp-header-top">
          <button className="back-btn" onClick={onBack}>{t.back}</button>
          <button className="lang-toggle-dp" onClick={handleLanguageToggle}>
            {language === 'en' ? 'मराठी' : 'EN'}
          </button>
        </div>
        <div className="dp-header-center">
          <DonutProgress completed={completedTasks.size} total={TASKS.length} />
          <span className="dp-progress-text">{completedTasks.size}/{TASKS.length}</span>
        </div>
        <div className="dp-header-bottom">
          <div className="dp-left">
            <div className="dp-user-name">Virat Kohli</div>
            <div className="dp-date">Day 30</div>
          </div>
          <div className="training-day-badge">{t.day} 30</div>
        </div>
      </div>

      <div className="task-list">
        {TASKS.map(task => renderTaskCard(task))}
      </div>

      {currentExercise && (
        <EnhancedCamera
          exercise={currentExercise}
          userId=""
          clapUrl="/claps.mp3"
          onComplete={() => {
            handleTaskComplete(currentExercise.id);
            setCurrentExercise(null);
          }}
          onBack={() => setCurrentExercise(null)}
        />
      )}

      {showCelebration && (
        <div className="demo-celebration-overlay">
          <div className="demo-confetti">
            {Array.from({ length: 40 }).map((_, i) => (
              <span key={i} className={`demo-confetti-piece piece-${i % 5}`} style={{ left: `${(i * 2.5) % 100}%`, animationDelay: `${(i % 10) * 0.15}s` }} />
            ))}
          </div>
          <div className="demo-celebration-box">
            <div className="demo-celebration-emoji">🎉</div>
            <div className="demo-celebration-text">{t.shabash}</div>
            <button className="demo-celebration-btn" onClick={onBack}>
              {t.shabashBtn}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DemoFitnessPage;